// Adapter + hooks bridging backend /agent/cases to the case-centric
// FE pages, which were originally written against the mock data
// shape from agentMockData.ts. Keeping the mock shape stable means
// every page (Dashboard, CasesPortfolio, CaseDetail, …) consumes a
// single, stable type — only this file knows about the wire format.

import { useCallback, useEffect, useState } from "react";
import i18n from "@/i18n";
import {
  listCases as apiListCases,
  getCaseLeads as apiGetCaseLeads,
  getOverview as apiGetOverview,
  listSuggestions as apiListSuggestions,
  getCaseReport as apiGetCaseReport,
  generateCaseReport as apiGenerateCaseReport,
  type AgentCase as BackendAgentCase,
  type CaseStage,
  type CaseUrgency,
  type ChatThread,
  type Bid,
  type Viewing,
  type OverviewSnapshot,
  type Suggestion,
  type CaseReport,
} from "./proagentApi";
import type {
  AgentCase,
  PropertyCase,
  ClientCase,
  PropertyMetrics,
  ClientMetrics,
  Stage,
  Urgency,
  BuyerLead,
} from "./agentMockData";
import { getCaseShortlist, type ShortlistItem } from "./shortlistApi";

/**
 * Convert one backend case to the FE-facing AgentCase shape.
 * Property and client variants diverge on a handful of fields; the
 * common base is shared.
 */
export function adaptCase(c: BackendAgentCase): AgentCase {
  const initials = makeInitials(c.client_name);
  const lastActivity = relativeTime(c.updated_at);
  const base = {
    id: c.id,
    shortName: c.short_name || c.client_name || "Untitled",
    name: caseDisplayName(c),
    subject: c.subject || c.client_name || "",
    client: c.client_name || "—",
    clientInitials: initials,
    stage: c.stage as Stage,
    urgency: c.urgency as Urgency,
    lastActivity,
    nextAction: c.next_action || "",
    nextActionDue: c.next_action_due || "",
    nextActionOverdue: c.next_action_overdue ?? false,
    aiNote: c.ai_note || "",
    pipelineSnapshot: {} as Record<string, number>,
    tasks: 0, // backend doesn't track task count yet; surface 0 instead of mock numbers
  };

  if (c.case_type === "property") {
    const m: PropertyMetrics = c.property_metrics ?? {
      enquiries: 0, viewings: 0, interested: 0, offers: 0,
    };
    base.pipelineSnapshot = synthesizePropertyPipeline(m);
    const out: PropertyCase = {
      ...base,
      type: "property",
      askingPrice: c.listing_asking_price ?? 0,
      // seller_target_price is now a real listing field; 0 (unset)
      // renders as an em-dash, which is the honest empty state — the
      // agent fills it in via the Sale-terms editor.
      sellerExpectation: c.listing_seller_target_price ?? 0,
      daysOnMarket: c.listing_days_on_market ?? 0,
      metrics: m,
      listingId: c.listing_id ?? undefined,
      listingLabel: c.listing_unit_label
        ? `${c.listing_project_name ?? ""} · ${c.listing_unit_label}`.trim()
        : (c.listing_project_name ?? undefined),
    };
    return out;
  }

  const m: ClientMetrics = c.client_metrics ?? {
    shortlisted: 0, viewed: 0, interested: 0, rejected: 0,
  };
  base.pipelineSnapshot = synthesizeClientPipeline(m);
  const out: ClientCase = {
    ...base,
    type: "client",
    budget: c.budget_sgd ?? 0,
    daysActive: daysSince(c.created_at),
    metrics: m,
    clientUserId: c.client_user_id ?? undefined,
  };
  return out;
}

// Uses the global i18n instance (not a hook) because this runs in the
// wire→FE mapping layer, outside React. A language switch re-fetches the
// case list, which re-maps, so the localised name follows the UI locale.
function caseDisplayName(c: BackendAgentCase): string {
  if (c.case_type === "property") {
    const proj = c.listing_project_name || c.short_name || i18n.t("agent.caseName.listingFallback");
    const unit = c.listing_unit_label ? ` ${c.listing_unit_label}` : "";
    return i18n.t("agent.caseName.sell", { name: `${proj}${unit}` });
  }
  return i18n.t("agent.caseName.help", {
    client: c.client_name || c.short_name || i18n.t("agent.caseName.buyerFallback"),
  });
}

function makeInitials(name: string): string {
  if (!name) return "—";
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p.charAt(0).toUpperCase()).join("");
}

export function relativeTime(iso: string): string {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  const diffMin = Math.max(0, (Date.now() - t) / 60000);
  if (diffMin < 60) return `${Math.round(diffMin)} min ago`;
  const diffHr = diffMin / 60;
  if (diffHr < 24) return `${Math.round(diffHr)} hrs ago`;
  const diffDay = diffHr / 24;
  if (diffDay < 7) return `${Math.round(diffDay)} days ago`;
  return new Date(iso).toLocaleDateString();
}

function daysSince(iso: string): number {
  if (!iso) return 0;
  return Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 86400000));
}

// Synthesize a coarse pipeline snapshot from metrics so the
// dashboard's pipeline-snapshot pills have something to draw. Real
// per-stage thread / viewing counts will replace this when the
// case-detail module endpoints land.
function synthesizePropertyPipeline(m: PropertyMetrics): Record<string, number> {
  const out: Record<string, number> = {};
  const newEnquiry = Math.max(0, m.enquiries - m.viewings);
  if (newEnquiry > 0) out["New Enquiry"] = newEnquiry;
  const viewedNotInterested = Math.max(0, m.viewings - m.interested);
  if (viewedNotInterested > 0) out.Viewed = viewedNotInterested;
  if (m.interested > 0) out.Interested = m.interested;
  if (m.offers > 0) out.Offer = m.offers;
  return out;
}

function synthesizeClientPipeline(m: ClientMetrics): Record<string, number> {
  const out: Record<string, number> = {};
  if (m.shortlisted > 0) out.Candidate = m.shortlisted - m.viewed - m.rejected;
  if (m.viewed > 0) out.Viewed = m.viewed - m.interested;
  if (m.interested > 0) out.Interested = m.interested;
  return out;
}

// ── React hook: list all cases ──────────────────────────────────────────────

export interface CasesResult {
  cases: AgentCase[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

// Cross-component invalidation pubsub. Every useCases() instance
// subscribes; any single instance calling refresh() bumps the
// shared "global tick" that all subscribers depend on, so a stage
// edit in CaseDetail re-fetches the sidebar / dashboard / portfolio
// in the same render pass. R4 caught the stale-sibling-component
// problem: per-instance tick state meant only the editor refreshed,
// the StagePill in the sidebar stayed at the old value until the
// next page navigation forced a remount.
//
// We don't reach for SWR / React Query here because (a) it's a
// single resource, (b) we want zero extra deps, (c) the surface is
// small enough that a 12-line pubsub is the right granularity.
let globalTick = 0;
const tickSubscribers = new Set<() => void>();
function bumpGlobalTick() {
  globalTick += 1;
  // Invalidating cases also invalidates the overview (under_offer
  // count etc. shifts with case mutations) and suggestions (the
  // ranking is computed from case state). Clear all three caches so
  // sub-tick fetches don't return stale data from the dedup map.
  inflightCasesByKey.clear();
  inflightOverview = null;
  inflightSuggestions = null;
  tickSubscribers.forEach((fn) => fn());
}

// In-flight request dedup. When sidebar + dashboard + portfolio
// mount in the same render pass they all kick off useCases() with
// the same opts, fanning out into N identical GET /agent/cases
// requests. Stash the in-flight promise per-options-key so all
// hooks share the same one. The map gets cleared on every
// invalidation tick so the next fetch is fresh.
const inflightCasesByKey = new Map<string, Promise<BackendAgentCase[]>>();
function dedupedListCases(opts: Parameters<typeof apiListCases>[0]): Promise<BackendAgentCase[]> {
  const key = JSON.stringify(opts ?? {});
  const existing = inflightCasesByKey.get(key);
  if (existing) return existing;
  const p = apiListCases(opts).finally(() => {
    // Drop after settle so the very next mount can issue a fresh
    // fetch (the previous one's data is already in React state of
    // the prior callers; new mounts shouldn't reuse it).
    inflightCasesByKey.delete(key);
  });
  inflightCasesByKey.set(key, p);
  return p;
}

let inflightOverview: Promise<OverviewSnapshot> | null = null;
function dedupedGetOverview(): Promise<OverviewSnapshot> {
  if (inflightOverview) return inflightOverview;
  const p = apiGetOverview().finally(() => {
    inflightOverview = null;
  });
  inflightOverview = p;
  return p;
}

// Same dedup pattern as overview: dashboard mounts both useOverview
// and useSuggestions in the same render pass, plus refreshes on
// every case mutation. Holding a single in-flight promise per tick
// avoids fanning out into multiple identical /agent/suggestions
// requests.
let inflightSuggestions: Promise<Suggestion[]> | null = null;
function dedupedListSuggestions(limit?: number): Promise<Suggestion[]> {
  if (inflightSuggestions) return inflightSuggestions;
  const p = apiListSuggestions(limit).finally(() => {
    inflightSuggestions = null;
  });
  inflightSuggestions = p;
  return p;
}

// invalidateCases is the public form of bumpGlobalTick — exposed so
// non-hook code paths (raw API calls in pages outside the case
// surface) can announce that the cases list is now stale.
//
// R10 caught the missing wiring: ArchiveListing on the backend
// transactionally archives the linked property case, but the
// FE's archiveListing() helper didn't tell useCases instances to
// re-fetch. Result: agent archives a listing, navigates to
// /pro/cases, and still sees the stale case until something else
// happens to mutate cases. Calling invalidateCases() after any
// listing mutation that may cascade to cases keeps the view fresh.
export function invalidateCases(): void {
  bumpGlobalTick();
}

export function useCases(opts?: {
  type?: "property" | "client";
  stage?: CaseStage;
  urgency?: CaseUrgency;
}): CasesResult {
  const [cases, setCases] = useState<AgentCase[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [, setLocalTick] = useState(0);

  // Subscribe to the global tick on mount; unsubscribe on unmount.
  useEffect(() => {
    const fn = () => setLocalTick((t) => t + 1);
    tickSubscribers.add(fn);
    return () => {
      tickSubscribers.delete(fn);
    };
  }, []);

  // Stringify opts so we don't churn on object identity.
  const key = `${opts?.type ?? ""}|${opts?.stage ?? ""}|${opts?.urgency ?? ""}`;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    dedupedListCases(opts)
      .then((rows) => {
        if (cancelled) return;
        setCases(rows.map(adaptCase));
        setLoading(false);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, globalTick]);

  return {
    cases,
    loading,
    error,
    refresh: bumpGlobalTick,
  };
}

// findCaseFromList replaces the mock's findCase() by ID on a CASES
// array. Pages that already have the case list call this; pages
// that mount on a case-detail URL with no list in scope should use
// useCase(id) below.
export function findCaseInList(cases: AgentCase[], id: string | undefined): AgentCase | undefined {
  if (!id) return undefined;
  return cases.find((c) => c.id === id);
}

// ── F3: real case leads ────────────────────────────────────────────────────

// useCaseLeads returns the buyer-side activity for a property case.
// The GET /agent/cases/:id/leads bundle carries three arrays — chat
// threads, viewings, and bids — and this hook surfaces all three:
//
//   - leads:    threads adapted into the mock's BuyerLead shape so the
//               existing PropertyPipeline component needs no refactor.
//               Bids are projected onto threads by phone-number match
//               (best-effort) so the "verbal offer" column populates.
//   - viewings: raw Viewing rows for the Viewings module's table.
//   - bids:     raw Bid rows for the Offers module's table.
//
// Fields the BuyerLead mock expects but the thread schema doesn't
// carry — interest, next-action notes, kanban column — are synthesized
// from the thread's status + bid presence. Coarse, but it matches what
// agents would write into a CRM column.
//
// refresh() forces a re-fetch — used after a mutation elsewhere in the
// case surface (e.g. logging viewing feedback) so the table re-hydrates
// without a full page reload.
export function useCaseLeads(caseId: string | undefined): {
  leads: BuyerLead[];
  viewings: Viewing[];
  bids: Bid[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
} {
  const [leads, setLeads] = useState<BuyerLead[]>([]);
  const [viewings, setViewings] = useState<Viewing[]>([]);
  const [bids, setBids] = useState<Bid[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const refresh = useCallback(() => setTick((n) => n + 1), []);
  useEffect(() => {
    if (!caseId) {
      setLeads([]);
      setViewings([]);
      setBids([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    apiGetCaseLeads(caseId)
      .then((bundle) => {
        if (cancelled) return;
        const bidsByPhone = new Map<string, Bid>();
        for (const b of bundle.bids) {
          const k = (b.bidder_phone || "").replace(/\D/g, "");
          if (k) bidsByPhone.set(k, b);
        }
        setLeads(bundle.threads.map((t) => threadToBuyerLead(t, bidsByPhone)));
        setViewings(bundle.viewings ?? []);
        setBids(bundle.bids ?? []);
        setLoading(false);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [caseId, tick]);
  return { leads, viewings, bids, loading, error, refresh };
}

// useCaseShortlist pulls /agent/cases/{id}/shortlist — the buyer's
// candidate properties on a client case. Mirrors useCaseLeads: manual
// state + a tick-based refresh() so a mutation elsewhere on the case
// surface can force a re-hydrate without a full page reload.
export function useCaseShortlist(caseId: string | undefined): {
  items: ShortlistItem[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
} {
  const [items, setItems] = useState<ShortlistItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const refresh = useCallback(() => setTick((n) => n + 1), []);
  useEffect(() => {
    if (!caseId) {
      setItems([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    getCaseShortlist(caseId)
      .then((rows) => {
        if (cancelled) return;
        setItems(rows);
        setLoading(false);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [caseId, tick]);
  return { items, loading, error, refresh };
}

function threadToBuyerLead(t: ChatThread, bidsByPhone: Map<string, Bid>): BuyerLead {
  const phoneKey = (t.contact_phone || "").replace(/\D/g, "");
  const matchedBid = phoneKey ? bidsByPhone.get(phoneKey) : undefined;
  // `status` is the lead's temperature, surfaced as an interest badge.
  const interest =
    t.status === "warm" || t.status === "converted" ? "High" :
    t.status === "lead" ? "Medium" :
    t.status === "cold" ? "Low" :
    "Unknown";
  return {
    id: t.id,
    name: t.contact_name || "(no name)",
    source: t.conversation_id ? "ProProp Chat" : "Direct buyer",
    budget: null,
    status: t.status,
    feedback: t.notes || "",
    interest,
    offer: matchedBid ? `${matchedBid.status} S$${matchedBid.amount.toLocaleString()}` : "—",
    next: "",
    // The kanban column is the thread's own pipeline_stage — a single,
    // agent-owned field — not a precedence guess over status + bids.
    col: t.pipeline_stage,
  };
}

// ── case reports (seller / buyer report generation) ───────────────────────

// useCaseReport drives the Report module on the property/client case
// surfaces. On mount it GETs the persisted report (null until the
// agent first generates one); generate() runs the backend LLM pass
// and swaps in the result.
//
// The POST response shape is identical to the GET, so the optimistic
// state set by generate() matches what a page refresh re-hydrates —
// no divergence between live-committed and post-refresh views.
export function useCaseReport(caseId: string | undefined): {
  report: CaseReport | null;
  loading: boolean;     // initial GET in flight
  generating: boolean;  // POST (generation) in flight
  error: string | null;
  generate: () => Promise<void>;
} {
  const [report, setReport] = useState<CaseReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!caseId) {
      setReport(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    apiGetCaseReport(caseId)
      .then((r) => {
        if (cancelled) return;
        setReport(r);
        setLoading(false);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [caseId]);

  const generate = useCallback(async () => {
    if (!caseId) return;
    setGenerating(true);
    setError(null);
    try {
      const r = await apiGenerateCaseReport(caseId);
      setReport(r);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setGenerating(false);
    }
  }, [caseId]);

  return { report, loading, generating, error, generate };
}

// ── F5: dashboard overview ─────────────────────────────────────────────────

// useOverview pulls /agent/overview, the same KPI bundle the agent
// workspace's old dashboard used. Today_viewings, overdue_viewings,
// hot_listings, recent_leads — used by the case-centric Dashboard
// to replace static mock data with real numbers. Subscribes to the
// shared invalidateCases pubsub so listing/case mutations refresh
// the snapshot too (overview counts change with both).
export function useOverview(): {
  snap: OverviewSnapshot | null;
  loading: boolean;
  error: string | null;
} {
  const [snap, setSnap] = useState<OverviewSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [, setLocalTick] = useState(0);

  useEffect(() => {
    const fn = () => setLocalTick((t) => t + 1);
    tickSubscribers.add(fn);
    return () => { tickSubscribers.delete(fn); };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    dedupedGetOverview()
      .then((s) => {
        if (cancelled) return;
        setSnap(s);
        setLoading(false);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
        setLoading(false);
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalTick]);

  return { snap, loading, error };
}

// useSuggestions pulls /agent/suggestions, the rule-based "AI
// suggestions" feed for the dashboard. Re-fetches on the shared
// invalidateCases tick — every case mutation can re-rank the list
// (e.g. saving a next_action_due moves the case out of overdue).
//
// Backend ships structural facts only (kind, action, case ref);
// callers render copy via i18n keys. Limit defaults to 5 to match
// the dashboard slot, but callers can override (a future "all
// suggestions" page would pass a higher limit).
export function useSuggestions(limit: number = 5): {
  suggestions: Suggestion[];
  loading: boolean;
  error: string | null;
} {
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [, setLocalTick] = useState(0);

  useEffect(() => {
    const fn = () => setLocalTick((t) => t + 1);
    tickSubscribers.add(fn);
    return () => { tickSubscribers.delete(fn); };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    dedupedListSuggestions(limit)
      .then((rows) => {
        if (cancelled) return;
        setSuggestions(rows);
        setLoading(false);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
        setLoading(false);
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [limit, globalTick]);

  return { suggestions, loading, error };
}

// Display helpers — case data sourced from agent_listings can have
// missing asking_price / listed_at, which Round 5 caught surfacing
// as "$0.00M asking" and "0d on market" — visually indistinguishable
// from a free home listed today. We render an em-dash for missing
// values instead. The threshold is "> 0" because zero is also not a
// meaningful value for either field in the SG residential market.

export function fmtSGDMillion(v: number | null | undefined): string {
  if (v == null || v <= 0) return "—";
  return `$${(v / 1_000_000).toFixed(2)}M`;
}

export function fmtDaysOnMarket(n: number | null | undefined): string {
  if (n == null || n <= 0) return "—";
  return `${n}d`;
}
